import { useState, useContext } from 'react';
import { FormHelperText, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttFormCheck,
    SttFormPeriod,
    SttFormText,
    SttFormTextarea,
    SttSelectFixedInterval,
    SttFormPrice,
    SelectGroupField,
    SttFormSelect,
    SttFormSwitch,
} from '../../../sporttia/all';
import type { DialogButton } from '../../../sporttia/dialogs/SttDialog';
import { parsePrice } from '../../../lib/utils';
import ActivityRateDiscountList from './ActivityDiscountList';
import constants from '../../../config/constants';
import { PERMISSIONS } from '../../../lib/permissions';

type Props = {
    open: boolean;
    item: Rate;
    activityId: number;
    onClose: () => void;
    onSave: (id?: number) => void;
    deleteRateEnabled?: boolean;
};

type Rate = {
    id?: number;
    name?: string;
    description?: string;
    ini?: string;
    end?: string;
    maxStudents?: number;
    onlyCertified?: boolean;
    signupOnline?: boolean;
    enrollment?: number | string;
    fee?: number | string;
    period?: string;
    tpvEnabled?: boolean;
    chargeFirstFeeOnSignup?: boolean;
    prorateFirstFee?: boolean;
    feeTaxConceptCode?: string | null;
    signupTaxConceptCode?: string | null;
    maxAge?: number | null;
    minAge?: number | null;
    mandatory?: {
        id: number | string | null;
    };
};

/**
 * ActivityRateDialog - dialog usado para añadir y editar tarifas de actividades
 * bool open - si está abierto o no
 * bool item - la tarifa que estamos viendo (si no contiene id ha de ser una tarifa que estamos creando)
 * int activityId - id de la actividad padre
 * Function onClose - callback al cerrar el dialog
 * Function onSave - callback al crear, guardar o borrar la tarifa actual
 */
export default function ActivityRateDialog({
    open,
    item,
    activityId,
    onClose,
    onSave,
    deleteRateEnabled = true,
}: Props): React.ReactElement | null {
    const [rate, setRate] = useState<Rate>(item);

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    function handleChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        setRate({ ...rate, [name]: value });
    }

    function saveAndBye(): void {
        cxt?.showMessage('S', t('OperationSuccessful'));

        onSave();
        onClose();
    }

    function saveRate(): void {
        // Validate that period is set when fee is greater than 0
        const feeValue = parseFloat(String(rate.fee ?? 0).replace(',', '.'));
        const isPeriodEmpty =
            !rate.period || rate.period === '0000-00-00 00:00:00';

        if (feeValue > 0 && isPeriodEmpty) {
            cxt?.showMessage('W', t('validation.feeIntervalRequired'));
            return;
        }

        // Normaliza un campo de precio (fee / enrollment) para el payload.
        // CU-869dfjf6q: vaciar la casilla emite '' (string vacío). Antes el
        // guard `value ? ... : undefined` trataba '' igual que "no tocado" y
        // omitía la clave del PUT; como el backend hace update parcial, el
        // valor anterior se quedaba (de ahí el workaround de escribir un 0).
        // - undefined / null  -> campo no establecido: se omite (undefined).
        // - '' (vaciado)       -> el usuario quiere borrarlo: se envía 0.
        // - resto              -> parsePrice del valor introducido.
        const normalizePrice = (
            value: number | string | null | undefined,
        ): string | undefined => {
            if (value === undefined || value === null) return undefined;
            if (value === '') return parsePrice(0) ?? undefined;
            return parsePrice(value) ?? undefined;
        };

        const params: Omit<Partial<Rate>, 'mandatory'> & {
            idActivity?: number;
            mandatory?: { id: number | string } | null;
        } = {
            name: rate.name,
            description: rate.description,
            ini: rate.ini,
            end: rate.end,
            maxStudents: rate.maxStudents,
            onlyCertified: rate.onlyCertified,
            signupOnline: rate.signupOnline,
            enrollment: normalizePrice(rate.enrollment),
            fee: normalizePrice(rate.fee),
            period: rate.period,
            tpvEnabled: rate.tpvEnabled,
            chargeFirstFeeOnSignup: rate.chargeFirstFeeOnSignup ?? false,
            prorateFirstFee: rate.prorateFirstFee ?? false,
            feeTaxConceptCode: rate.feeTaxConceptCode ?? null,
            signupTaxConceptCode: rate.signupTaxConceptCode ?? null,
            maxAge: rate?.maxAge === 0 ? null : rate?.maxAge,
            minAge: rate?.minAge === 0 ? null : rate?.minAge,
        };

        if (rate.mandatory?.id) {
            params.mandatory = { id: rate.mandatory.id };
        } else if (rate.id && item.mandatory?.id) {
            // Only send { id: '' } to remove an existing mandatory group
            params.mandatory = { id: '' };
        }

        if (rate.id) {
            cxt?.api('PUT', `/activities/rates/${rate.id}`, {
                params,
                success: () => {
                    saveAndBye();
                },
            });
        } else {
            if (!activityId) {
                return;
            }

            cxt?.api('POST', `/activities/${activityId}/rates`, {
                params,
                success: () => {
                    saveAndBye();
                },
            });
        }
    }

    function deleteRate(): void {
        cxt?.api('DELETE', `/activities/rates/${rate.id}`, {
            confirmation: true,
            success: () => {
                saveAndBye();
            },
        });
    }

    if (!rate) {
        return null;
    }

    const buttons: (DialogButton | false)[] = [
        // si el rate es uno existente, añadimos el botón borrar primero
        rate.id && deleteRateEnabled
            ? {
                  type: 'delete' as const,
                  onClick: deleteRate,
                  permission: PERMISSIONS.ACTIVITY.MODIFY,
              }
            : false,
        {
            type: 'save' as const,
            onClick: saveRate,
            permission: PERMISSIONS.ACTIVITY.MODIFY,
        },
    ];

    return (
        <SttDialog
            title={t('Rate')}
            open={open}
            maxWidth="md"
            buttons={buttons}
            onClose={onClose}
            content={
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <SttFormText
                                    name="name"
                                    caption={t('Name')}
                                    defVal={rate.name}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <SttFormTextarea
                                    name="description"
                                    caption={t('Description')}
                                    defVal={rate.description}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <SttFormPeriod
                                    captionIni={t('activity.rateStart')}
                                    helpIni={t('activity.rateStartHelp')}
                                    captionEnd={t('activity.rateEnd')}
                                    helpEnd={t('activity.rateEndHelp')}
                                    nameIni="ini"
                                    defValIni={rate.ini}
                                    nameEnd="end"
                                    defValEnd={rate.end}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <SelectGroupField
                                    caption={t('MandatoryGroup')}
                                    name="mandatory"
                                    defVal={
                                        rate.mandatory as
                                            | { id: number; name: string }
                                            | null
                                            | undefined
                                    }
                                    onChange={handleChange}
                                    onlyActive
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <SttFormText
                                    caption={t('MaxUsers')}
                                    name="maxStudents"
                                    defVal={rate.maxStudents?.toString()}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <SttFormPrice
                                    name="enrollment"
                                    caption={t('Enrollment')}
                                    defVal={rate.enrollment as string}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <SttFormPrice
                                    name="fee"
                                    caption={t('Fee')}
                                    defVal={rate.fee as string}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <SttSelectFixedInterval
                                    name="period"
                                    caption={t('generateFeesEvery')}
                                    options={[
                                        'FORTNIGHT',
                                        '1 MONTH',
                                        '2 MONTH',
                                        '3 MONTH',
                                        '4 MONTH',
                                        '6 MONTH',
                                        '1 YEAR',
                                    ]}
                                    defVal={rate.period as string}
                                    onChange={handleChange}
                                    required={
                                        parseFloat(
                                            String(rate.fee ?? 0).replace(
                                                ',',
                                                '.',
                                            ),
                                        ) > 0
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 6, xl: 4 }}>
                                <SttFormSelect
                                    caption={t('activity.minAge')}
                                    name="minAge"
                                    defVal={rate.minAge ?? t('generic.empty')}
                                    onChange={(value: {
                                        value: unknown;
                                        name: string | undefined;
                                    }) => {
                                        handleChange({
                                            name: value.name,
                                            value: value.value,
                                        });
                                    }}
                                    options={[
                                        {
                                            label: t('generic.empty'),
                                            value: 0,
                                        },
                                        ...[...Array(90).keys()]
                                            .slice(1)
                                            .map((value: any) => ({
                                                label: value,
                                                value,
                                            })),
                                    ]}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 6, xl: 4 }}>
                                <SttFormSelect
                                    caption={t('activity.maxAge')}
                                    name="maxAge"
                                    defVal={rate.maxAge ?? t('generic.empty')}
                                    onChange={(value: {
                                        value: unknown;
                                        name: string | undefined;
                                    }) => {
                                        handleChange({
                                            name: value.name,
                                            value: value.value,
                                        });
                                    }}
                                    options={[
                                        {
                                            label: t('generic.empty'),
                                            value: 0,
                                        },
                                        ...[...Array(90).keys()]
                                            .slice(1)
                                            .map((value: any) => ({
                                                label: value,
                                                value,
                                            })),
                                    ]}
                                />
                            </Grid>
                            {Object.values(constants.orgt.scIds).includes(
                                cxt?.sc?.id as 3444 | 2401,
                            ) && (
                                <Grid size={{ md: 6, sm: 6, xs: 6 }}>
                                    <SttFormSelect
                                        name="feeTaxConceptCode"
                                        caption={t('orgt.feeTaxConcept')}
                                        defVal={
                                            rate?.feeTaxConceptCode ??
                                            constants.orgt.taxCodes.noOption
                                        }
                                        options={[
                                            {
                                                caption: t('orgt.noOption'),
                                                value: null,
                                            },
                                            {
                                                caption: t('orgt.feePass'),
                                                value: constants.orgt.taxCodes
                                                    .feePassCode,
                                            },
                                            {
                                                caption: t(
                                                    'orgt.feeRoomActivity',
                                                ),
                                                value: constants.orgt.taxCodes
                                                    .feeRoomActivity,
                                            },
                                            {
                                                caption: t(
                                                    'orgt.feeAquaticActivity',
                                                ),
                                                value: constants.orgt.taxCodes
                                                    .feeAquaticActivity,
                                            },
                                            {
                                                caption: t(
                                                    'orgt.athleticismFee',
                                                ),
                                                value: constants.orgt.taxCodes
                                                    .athleticismFee,
                                            },
                                            {
                                                caption: t('orgt.summerFees'),
                                                value: constants.orgt.taxCodes
                                                    .summerFees,
                                            },
                                            {
                                                caption: t(
                                                    'orgt.facilityRental',
                                                ),
                                                value: constants.orgt.taxCodes
                                                    .facilityRental,
                                            },
                                        ]}
                                        onChange={(value: {
                                            name: string | undefined;
                                            value: unknown;
                                        }) => handleChange(value)}
                                    />
                                </Grid>
                            )}
                            {Object.values(constants.orgt.scIds).includes(
                                cxt?.sc?.id as 3444 | 2401,
                            ) && (
                                <Grid size={{ md: 6, sm: 6, xs: 6 }}>
                                    <SttFormSelect
                                        name="signupTaxConceptCode"
                                        caption={t('orgt.signupTaxConcept')}
                                        defVal={
                                            rate?.signupTaxConceptCode ??
                                            constants.orgt.taxCodes.noOption
                                        }
                                        options={[
                                            {
                                                caption: t('orgt.noOption'),
                                                value: null,
                                            },
                                            {
                                                caption: t('orgt.enrollment'),
                                                value: constants.orgt.taxCodes
                                                    .enrollment,
                                            },
                                        ]}
                                        onChange={(value: {
                                            name: string | undefined;
                                            value: unknown;
                                        }) => handleChange(value)}
                                    />
                                </Grid>
                            )}
                            <Grid size={{ xs: 12 }}>
                                <SttFormSwitch
                                    name="prorateFirstFee"
                                    caption={t('activity.prorateFirstFee')}
                                    checked={!!rate.prorateFirstFee}
                                    onChange={handleChange}
                                />
                                <FormHelperText>
                                    {t('activity.prorateFirstFeeHelp')}
                                </FormHelperText>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <div>
                                    <SttFormCheck
                                        name="onlyCertified"
                                        caption={t('OnlyCertified')}
                                        checked={!!rate.onlyCertified}
                                        onChange={(value: {
                                            name: string;
                                            value: unknown;
                                        }) => handleChange(value)}
                                    />
                                </div>
                                <div>
                                    <SttFormCheck
                                        name="signupOnline"
                                        caption={t('page.homeA.at.1')}
                                        checked={!!rate.signupOnline}
                                        onChange={(value: {
                                            name: string;
                                            value: unknown;
                                        }) => handleChange(value)}
                                    />
                                </div>
                                <div>
                                    <SttFormCheck
                                        name="tpvEnabled"
                                        caption={t('page.homeA.bk.1')}
                                        checked={!!rate.tpvEnabled}
                                        disabled={!rate.signupOnline}
                                        onChange={(value: {
                                            name: string;
                                            value: unknown;
                                        }) => handleChange(value)}
                                    />
                                </div>
                                {rate.signupOnline && (
                                    <div>
                                        <SttFormCheck
                                            name="chargeFirstFeeOnSignup"
                                            caption={t(
                                                'page.sc.group.chargeFirstFeeOnSignup',
                                            )}
                                            checked={
                                                !!rate.chargeFirstFeeOnSignup
                                            }
                                            onChange={(value: {
                                                name: string;
                                                value: unknown;
                                            }) => handleChange(value)}
                                        />
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Grid container>
                            <Grid size={{ xs: 12 }}>
                                <ActivityRateDiscountList
                                    rateId={item.id as number}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            }
        />
    );
}
